import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';

/**
 * „Upuść plik tutaj” (owner 2026-11-05 09:44: „dodawanie plikow musi byc poprzez upuszczanie takze - upusc plik tutaj
 * pole”, everywhere a file is added). A field around the place's existing file input (which stays the way to pick, also
 * with the keyboard and a screen reader): dragging a file over highlights it, dropping it does what picking does —
 * `onFiles` gets the same files the input's change would give, after the same `accept` and one-or-many check the input
 * does. A wrong type or several files where one is taken: a Polish message here and nothing happens.
 *
 * - Only drags of files (`types` has „Files”, also in Safari) are touched: row reordering and other drags are left alone.
 * - While a drop field is on the page, a file dropped anywhere else is not opened by the browser.
 * - In a real browser the dropped file is also put into the input (the input shows its name; `required` is met).
 */
type Props = {
  onFiles: (files: File[]) => void;
  /** The input's own `accept`. */
  accept?: string;
  /** The input takes several files (e.g. bank statements). */
  multiple?: boolean;
  /** No drop while off or sending. */
  disabled?: boolean;
  /** A smaller field (e.g. inside a table cell). */
  compact?: boolean;
  children: ReactNode;
};

const ZONE = 'file-drop';

function isFileDrag(e: { dataTransfer?: DataTransfer | null }): boolean {
  const types = e.dataTransfer?.types;
  return !!types && Array.from(types).includes('Files');
}

// the page-wide guard: a file dropped outside every drop field is not opened (only while a field is shown)
let mounted = 0;
function guard(e: globalThis.DragEvent) {
  if (!isFileDrag(e)) return;
  if (e.target instanceof Element && e.target.closest(`.${ZONE}`)) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
}
function useOutsideGuard() {
  useEffect(() => {
    if (mounted++ === 0) {
      window.addEventListener('dragover', guard);
      window.addEventListener('drop', guard);
    }
    return () => {
      if (--mounted === 0) {
        window.removeEventListener('dragover', guard);
        window.removeEventListener('drop', guard);
      }
    };
  }, []);
}

const tokens = (accept: string | undefined) =>
  (accept ?? '')
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

/** Does the file fit the input's `accept` (extensions, exact types, „image/*”)? No `accept`: any file. */
export function acceptsFile(file: File, accept: string | undefined): boolean {
  const list = tokens(accept);
  if (list.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return list.some((t) => (t.startsWith('.') ? name.endsWith(t) : t.endsWith('/*') ? type.startsWith(t.slice(0, -1)) : type === t));
}

const TYPE_NAMES: Record<string, string> = {
  'image/*': 'zdjęcia',
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/heic': 'HEIC',
  'application/xml': 'XML',
  'text/xml': 'XML',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  '.jpeg': 'JPG',
};

/** The allowed types named for the message, e.g. „PDF, JPG, PNG”. */
export function acceptLabels(accept: string | undefined): string {
  const names = tokens(accept).map((t) => TYPE_NAMES[t] ?? (t.startsWith('.') ? t.slice(1).toUpperCase() : (t.split('/')[1] ?? t).toUpperCase()));
  return [...new Set(names)].join(', ');
}

export function FileDrop({ onFiles, accept, multiple = false, disabled = false, compact = false, children }: Props) {
  useOutsideGuard();
  const zone = useRef<HTMLDivElement>(null);
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const [message, setMessage] = useState('');
  const text = multiple ? 'Upuść pliki tutaj' : 'Upuść plik tutaj';

  function enter(e: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(e) || disabled) return;
    depth.current += 1;
    setOver(true);
  }
  function leave(e: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(e) || disabled) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }
  function dragOver(e: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
  }
  function drop(e: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    if (disabled) return;
    const list = Array.from(e.dataTransfer.files ?? []);
    if (list.length === 0) return;
    if (!multiple && list.length > 1) return setMessage('Upuść jeden plik.');
    if (list.some((f) => !acceptsFile(f, accept))) return setMessage(`Ten plik ma zły typ – dozwolone: ${acceptLabels(accept)}.`);
    setMessage('');
    const input = zone.current?.querySelector<HTMLInputElement>('input[type="file"]');
    if (input) {
      try {
        input.files = e.dataTransfer.files; // the input shows the dropped file, as if picked
      } catch {
        // not a real FileList (tests) — the handler below is what counts
      }
    }
    onFiles(list);
  }

  return (
    <div
      ref={zone}
      role="group"
      aria-label={text}
      className={`${ZONE}${compact ? ' compact' : ''}${over ? ' is-over' : ''}${disabled ? ' is-disabled' : ''}`}
      onDragEnter={enter}
      onDragLeave={leave}
      onDragOver={dragOver}
      onDrop={drop}
    >
      <span className="file-drop-text" aria-hidden="true">
        {text}
      </span>
      {children}
      {message && (
        <p className="alert" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}
